import {
  AppShell,
  DropdownMenuItem,
  DropdownMenuSeparator,
  toast,
  useTheme,
  type CommandGroup,
  type NavGroup,
  type NavItem,
  type RenderLink,
} from '@bixo/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  BellRing,
  ClipboardList,
  DatabaseBackup,
  HandCoins,
  ReceiptText,
  BookOpenText,
  Building2,
  CreditCard,
  Landmark,
  Scale,
  Percent,
  NotebookPen,
  Warehouse,
  Globe,
  LifeBuoy,
  Boxes,
  Building,
  FileSpreadsheet,
  FileText,
  Handshake,
  LayoutDashboard,
  LogOut,
  IdCard,
  MapPin,
  Moon,
  Package,
  Receipt,
  ScrollText,
  ShieldCheck,
  ShoppingCart,
  Store,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react'
import { useMemo } from 'react'
import { endpoints, tenantQuery } from '../lib/api'
import { can, useMe } from '../lib/auth'
import { AnnouncementBar, ImpersonationBanner } from '../modules/platform/banners'

const APP_PATHS = [
  '/',
  '/settings/users',
  '/settings/roles',
  '/settings/audit',
  '/settings/billing',
  '/settings/domains',
  '/settings/support',
  '/settings/business',
  '/settings/branches',
  '/products',
  '/catalog',
  '/parties',
  '/sites',
  '/employees',
  '/finance/accounts',
  '/finance/journal',
  '/finance/bank',
  '/purchases',
  '/inventory',
  '/labels',
  '/pricing',
  '/profile',
  '/pos',
  '/sales/invoices',
  '/sales/estimates',
  '/sales/orders',
  '/sales/approvals',
  '/receipts',
  '/receivables',
  '/settings/sales',
  '/settings/import',
  '/partners',
  '/partners/rules',
  '/partners/consignment',
  '/partners/portal',
  '/finance/statements',
  '/finance/channel-costs',
  '/reports',
  '/gst',
  '/settings/report-subscriptions',
] as const
type AppPath = (typeof APP_PATHS)[number]

function isAppPath(path: string | undefined): path is AppPath {
  return (APP_PATHS as readonly string[]).includes(path ?? '')
}

const renderLink: RenderLink = ({ item, className, children, onNavigate }) =>
  isAppPath(item.to) ? (
    <Link to={item.to} className={className} onClick={onNavigate}>
      {children}
    </Link>
  ) : null

export function AppLayout() {
  const me = useMe()
  const { data: tenant } = useQuery(tenantQuery)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toggle } = useTheme()

  const signOut = async (): Promise<void> => {
    try {
      await endpoints.logout()
    } catch {
      /* the session may already be gone; sign out locally regardless */
    }
    queryClient.clear()
    toast.success('Signed out')
    await navigate({ to: '/login' })
  }

  const groups = useMemo<NavGroup[]>(() => {
    const live = (id: string, label: string, icon: NavItem['icon'], to: AppPath): NavItem => ({
      id,
      label,
      icon,
      to,
      active: to === '/' ? pathname === '/' : pathname.startsWith(to),
    })
    const soon = (id: string, label: string, icon: NavItem['icon']): NavItem => ({
      id,
      label,
      icon,
      soon: true,
    })

    const settings: NavItem[] = [
      ...(can(me, 'settings.view') ? [live('business', 'Business', Building2, '/settings/business')] : []),
      ...(can(me, 'settings.view') ? [live('branches', 'Branches', Warehouse, '/settings/branches')] : []),
      ...(can(me, 'settings.view') || can(me, 'discount.approve')
        ? [live('sales-settings', 'Sales & documents', ReceiptText, '/settings/sales')]
        : []),
      ...(can(me, 'users.view') ? [live('users', 'Users', Users, '/settings/users')] : []),
      ...(can(me, 'users.view') ? [live('roles', 'Roles & access', ShieldCheck, '/settings/roles')] : []),
      ...(can(me, 'audit.view') ? [live('audit', 'Audit log', ScrollText, '/settings/audit')] : []),
      ...(can(me, 'settings.view')
        ? [live('billing', 'Plan & billing', CreditCard, '/settings/billing')]
        : []),
      ...(me.is_owner ? [live('domains', 'Custom domain', Globe, '/settings/domains')] : []),
      ...(me.is_owner ? [live('support', 'Support access', LifeBuoy, '/settings/support')] : []),
      ...(me.is_owner ? [live('import', 'Import from LedgerFlow', DatabaseBackup, '/settings/import')] : []),
      live('report-subscriptions', 'Report emails', BellRing, '/settings/report-subscriptions'),
    ]

    return [
      { label: 'Overview', items: [live('dashboard', 'Dashboard', LayoutDashboard, '/')] },
      {
        label: 'Sell',
        items: [
          ...(can(me, 'pos.view')
            ? [live('pos', 'Point of sale', Store, '/pos')]
            : [soon('pos', 'Point of sale', Store)]),
          ...(can(me, 'estimates.view')
            ? [live('estimates', 'Estimates', FileText, '/sales/estimates')]
            : []),
          ...(can(me, 'orders.view') ? [live('orders', 'Sales orders', ClipboardList, '/sales/orders')] : []),
          ...(can(me, 'sales.view')
            ? [live('invoices', 'Sales invoices', Receipt, '/sales/invoices')]
            : [soon('invoices', 'Sales invoices', Receipt)]),
          ...(can(me, 'receipts.view') ? [live('receipts', 'Money in & out', Wallet, '/receipts')] : []),
          ...(can(me, 'sales.view') || can(me, 'receipts.view')
            ? [live('receivables', 'Outstanding', HandCoins, '/receivables')]
            : []),
        ],
      },
      {
        label: 'Buy & stock',
        items: [
          ...(can(me, 'purchases.view')
            ? [live('purchases', 'Purchases', ShoppingCart, '/purchases')]
            : [soon('purchases', 'Purchases', ShoppingCart)]),
          ...(can(me, 'stock.view')
            ? [live('inventory', 'Inventory', Boxes, '/inventory')]
            : [soon('inventory', 'Inventory', Boxes)]),
          ...(can(me, 'items.view')
            ? [live('products', 'Products', Package, '/products')]
            : [soon('products', 'Products', Package)]),
        ],
      },
      {
        label: 'Network',
        items: [
          ...(can(me, 'parties.view') ? [live('parties', 'Parties', Building, '/parties')] : []),
          ...(can(me, 'partners.view')
            ? [live('partners', 'Partners & SIs', Handshake, '/partners')]
            : [soon('partners', 'Partners & SIs', Handshake)]),
          ...(can(me, 'partners.view') || can(me, 'orders.view')
            ? [live('partner-portal', 'Partner portal', Globe, '/partners/portal')]
            : []),
          ...(can(me, 'sites.view') ? [live('sites', 'Sites & projects', MapPin, '/sites')] : []),
          ...(can(me, 'employees.view')
            ? [live('employees', 'Employees & payroll', IdCard, '/employees')]
            : []),
        ],
      },
      {
        label: 'Finance',
        items: [
          ...(can(me, 'accounts.view')
            ? [live('ledgers', 'Accounts & ledgers', BookOpenText, '/finance/accounts')]
            : []),
          ...(can(me, 'accounts.view') ? [live('journal', 'Journal', NotebookPen, '/finance/journal')] : []),
          ...(can(me, 'settings.view') || can(me, 'accounts.view')
            ? [live('bank', 'Bank & cash', Landmark, '/finance/bank')]
            : []),
          ...(can(me, 'accounts.view')
            ? [live('statements', 'P&L & balance sheet', Scale, '/finance/statements')]
            : []),
          ...(can(me, 'partners.view')
            ? [live('channel-costs', 'Channel costs', Percent, '/finance/channel-costs')]
            : []),
          ...(can(me, 'reports.view')
            ? [live('reports', 'Reports', BarChart3, '/reports')]
            : [soon('reports', 'Reports', BarChart3)]),
          // No GST screens for a business that is not GST-registered.
          ...(me.gst_enabled === false
            ? []
            : can(me, 'gst.view')
              ? [live('gst', 'GST returns', FileSpreadsheet, '/gst')]
              : [soon('gst', 'GST returns', FileSpreadsheet)]),
        ],
      },
      ...(settings.length > 0 ? [{ label: 'Settings', items: settings }] : []),
    ]
  }, [me, pathname])

  const mobileTabs = useMemo<NavItem[]>(
    () => [
      { id: 'm-home', label: 'Home', icon: LayoutDashboard, to: '/', active: pathname === '/' },
      ...(can(me, 'pos.view')
        ? [{ id: 'm-pos', label: 'POS', icon: Store, to: '/pos', active: pathname === '/pos' }]
        : []),
      ...(can(me, 'sales.view')
        ? [
            {
              id: 'm-sell',
              label: 'Sales',
              icon: Receipt,
              to: '/sales/invoices',
              active: pathname.startsWith('/sales'),
            },
          ]
        : []),
      ...(can(me, 'users.view')
        ? [
            {
              id: 'm-users',
              label: 'Team',
              icon: Users,
              to: '/settings/users',
              active: pathname.startsWith('/settings'),
            },
          ]
        : []),
      { id: 'm-profile', label: 'Profile', icon: UserCog, to: '/profile', active: pathname === '/profile' },
    ],
    [me, pathname],
  )

  const commands = useMemo<CommandGroup[]>(() => {
    const pages = groups.flatMap((group) => group.items)
    return [
      {
        heading: 'Go to',
        items: pages
          .filter((item) => !item.soon && isAppPath(item.to))
          .map((item) => ({
            id: `go-${item.id}`,
            label: item.label,
            icon: item.icon,
            onSelect: () => {
              if (isAppPath(item.to)) void navigate({ to: item.to })
            },
          })),
      },
      {
        heading: 'Account',
        items: [
          {
            id: 'profile',
            label: 'Profile & security',
            icon: UserCog,
            keywords: ['2fa', 'password', 'two-factor'],
            onSelect: () => void navigate({ to: '/profile' }),
          },
          { id: 'theme', label: 'Toggle dark mode', icon: Moon, keywords: ['theme'], onSelect: toggle },
          { id: 'logout', label: 'Sign out', icon: LogOut, onSelect: () => void signOut() },
        ],
      },
      {
        heading: 'Coming soon',
        items: pages
          .filter((item) => item.soon)
          .map((item) => ({
            id: `soon-${item.id}`,
            label: item.label,
            icon: item.icon,
            hint: 'Soon',
            disabled: true,
            onSelect: () => undefined,
          })),
      },
    ]
    // signOut is stable enough for a palette; re-binding on every render is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, navigate, toggle])

  return (
    <AppShell
      brand={{
        name: tenant?.app_name ?? tenant?.name ?? 'Bixo',
        logoUrl: tenant?.logo_url ?? null,
        subtitle: tenant?.name && tenant.app_name !== tenant.name ? tenant.name : 'Business workspace',
      }}
      groups={groups}
      mobileTabs={mobileTabs}
      user={{ name: me.name, email: me.email }}
      renderLink={renderLink}
      commands={commands}
      userMenu={
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/profile">
              <UserCog />
              Profile & security
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={() => void signOut()}>
            <LogOut />
            Sign out
          </DropdownMenuItem>
        </>
      }
    >
      <div className="mb-5 grid gap-2 empty:hidden">
        <ImpersonationBanner />
        <AnnouncementBar />
      </div>
      <Outlet />
    </AppShell>
  )
}
